import React, { useState } from "react";
import { MinusIcon, PlusIcon } from "lucide-react";
import { BaseOption } from "@/constants/multilingual";

interface SelectorProps<T extends BaseOption> {
  options: T[];
  selectedValue: string;
  onChange: (option: T) => void;
  label?: string;
  truncateAt?: number;
  numColumns?: number;
  renderOption: (
    option: T,
    isSelected: boolean,
    onSelect: () => void,
  ) => React.ReactNode;
}

const Selector = <T extends BaseOption>({
  options,
  selectedValue,
  onChange,
  numColumns = 3,
  label = "options",
  truncateAt = 9,
  renderOption,
}: SelectorProps<T>) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const truncatedOptions = isExpanded ? options : options.slice(0, truncateAt);
  const remainingOptions = Math.abs(
    options.length - Math.min(truncateAt, options.length),
  );

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const gridColsMap: Record<number, string> = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
  };

  const gridCols = gridColsMap[numColumns] || "grid-cols-3";

  return (
    <>
      <div className={`w-full grid ${gridCols} gap-3 p-1`}>
        {truncatedOptions.map((option) => {
          const isSelected = selectedValue === option.value;
          const handleSelect = () => onChange(option);

          return (
            <div key={option.value} className="h-full">
              {renderOption(option, isSelected, handleSelect)}
            </div>
          );
        })}
      </div>
      <button
        onClick={toggleExpanded}
        className="text-lightgreen hover:text-[#018354] flex flex-row items-center justify-end gap-2 w-full py-2 px-4 text-sm"
      >
        {remainingOptions > 0 && (
          <>
            {isExpanded ? (
              <>
                <MinusIcon className="w-4 h-4" />
                <span>
                  Show {remainingOptions} fewer {label}
                </span>
              </>
            ) : (
              <>
                <PlusIcon className="w-4 h-4" />
                <span>
                  Show {remainingOptions} more {label}
                </span>
              </>
            )}
          </>
        )}
      </button>
    </>
  );
};

export default Selector;
